/**
 * MockupCalcTotal — propuesta del nuevo PANEL LATERAL grande con contorno (FASE 1a).
 *
 * Solo mockup visual, no toca lógica real. Datos hardcodeados (25.875 €).
 * Tema CLARO unificado con `Tu Análisis de Flota`.
 *
 * Cambio único respecto al original:
 *   • Panel lateral 220px → 320px
 *   • Fondo blanco/emerald-50 + borde emerald-200 + sombra + esquinas redondeadas
 *   • TOTAL pasa de text-[42px] → text-6xl (~60px)
 *   • Card del panel "despegada" del borde con margen
 */
import React from "react";
import { ChevronLeft, Phone, Download, Fuel, Wrench, Shield, TrendingUp, Leaf } from "lucide-react";

const MOBILSAFE_LOGO = "/logo-mobilsafe.png";

const MOCK = {
  ahorroCombustible: 17325,
  ahorroMantenimiento: 4500,
  ahorroSeguridad: 4050,
  ahorroTotal: 25875,
  nVehiculos: 25,
};
const personalizadoMin = Math.round(MOCK.ahorroTotal * 1.15);
const personalizadoMax = Math.round(MOCK.ahorroTotal * 1.20);
const delta = personalizadoMax - MOCK.ahorroTotal;

const eur = (n) => Math.max(0, Math.round(n || 0)).toLocaleString("es-ES") + " €";

// ============================================
// HEADER CON LOGO OFICIAL (mismo patrón que Landing.jsx)
// ============================================
const HeaderMobilsafe = () => (
  <header className="bg-white border-b border-slate-200 sticky top-0 z-30" data-testid="mock-header">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
      <img
        src={MOBILSAFE_LOGO}
        alt="MOBILSAFE"
        className="h-10 md:h-12"
        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
      />
      {/* Fallback (lo que actualmente se ve en la Landing real porque CloudFront bloquea el SVG) */}
      <div className="hidden items-center gap-2">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
          <Leaf className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-900">MOBILSAFE</h1>
          <p className="text-[10px] text-emerald-600 tracking-widest font-semibold">FLEET TECHNOLOGY</p>
        </div>
      </div>
      <div className="border-l border-slate-200 pl-3 ml-2">
        <p className="text-xs text-emerald-600 font-semibold tracking-wide uppercase">Calculadora de Ahorro</p>
      </div>
    </div>
  </header>
);

// ============================================
// PANEL LATERAL NUEVO — grande con contorno
// ============================================
const PanelLateralNuevo = () => (
  <aside
    className="hidden md:block flex-shrink-0 sticky self-start"
    style={{ width: "340px", top: "84px", padding: "24px 16px" }}
    data-testid="mock-panel-lateral-nuevo"
  >
    <div
      className="bg-white rounded-2xl border-2 border-emerald-200 shadow-lg shadow-emerald-500/10 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 30%)" }}
    >
      {/* Cabecera del panel */}
      <div className="px-5 pt-5 pb-2">
        <div className="flex items-center gap-1.5 mb-1">
          <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
          <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Ahorro acumulado</p>
        </div>
        <p className="text-5xl lg:text-6xl font-black leading-none text-emerald-600 tracking-tight" data-testid="mock-panel-total">
          {eur(MOCK.ahorroTotal)}
        </p>
        <p className="text-xs text-slate-500 mt-2">Calculado para {MOCK.nVehiculos} vehículos</p>
      </div>

      {/* Divisor */}
      <div className="border-t border-emerald-100 mx-5 my-3"></div>

      {/* Lista de áreas */}
      <div className="px-5 pb-5 space-y-3">
        {[
          { Icon: Fuel,  label: "Combustible",   val: MOCK.ahorroCombustible,  color: "text-amber-600", iconBg: "bg-amber-50" },
          { Icon: Wrench, label: "Mantenimiento", val: MOCK.ahorroMantenimiento, color: "text-violet-600", iconBg: "bg-violet-50" },
          { Icon: Shield, label: "Seguridad",     val: MOCK.ahorroSeguridad,    color: "text-rose-600",   iconBg: "bg-rose-50" },
        ].map((it, i) => (
          <div key={i} className="flex items-center gap-3" data-testid={`mock-panel-area-${i}`}>
            <div className={`${it.iconBg} w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0`}>
              <it.Icon className={`w-4 h-4 ${it.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500">{it.label}</p>
              <p className="text-base font-bold text-slate-900">{eur(it.val)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Footer reassurance */}
      <div className="bg-slate-50 border-t border-slate-100 px-5 py-3">
        <p className="text-[11px] text-slate-500 leading-relaxed">
          🧪 <strong className="text-slate-700">Piloto de 45-60 días</strong> antes de cualquier despliegue total.
        </p>
      </div>
    </div>
  </aside>
);

// ============================================
// CONTENIDO P5 (sin cambios respecto a la versión actual, solo en tema CLARO)
// ============================================
const ContenidoP5 = () => (
  <main className="flex-1 px-6 md:px-10 py-8" data-testid="mock-contenido">
    <div className="max-w-3xl mx-auto space-y-6">
      <button className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5">
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>

      <div>
        <p className="text-2xl md:text-3xl font-black leading-tight text-slate-900">
          El ahorro total estimado <span className="text-emerald-600">para tu flota</span>.
        </p>
        <p className="text-sm text-slate-500 mt-2">Calculado con la media del sector para flotas como la tuya.</p>
      </div>

      {/* Comparativa */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Comparativa</p>
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-slate-600">Media sector</span>
            <span className="text-lg font-bold text-slate-900">{eur(MOCK.ahorroTotal)}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-emerald-600">Análisis personalizado</span>
            <span className="text-lg font-bold text-emerald-600">{eur(personalizadoMin)} – {eur(personalizadoMax)}</span>
          </div>
          <div className="flex justify-end pt-1">
            <span className="text-xs font-bold text-emerald-600">+{eur(delta)} adicional</span>
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">
        Cada flota tiene su propia operativa: rutas, tipo de carga, perfil de conductores, historial de siniestros. Con un análisis de 30 minutos, identificamos exactamente dónde están tus oportunidades de ahorro reales.
      </p>

      {/* Desglose */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-3">
        {[
          { Icon: Fuel, label: "Combustible", total: MOCK.ahorroCombustible },
          { Icon: Wrench, label: "Mantenimiento", total: MOCK.ahorroMantenimiento },
          { Icon: Shield, label: "Seguridad", total: MOCK.ahorroSeguridad },
        ].map((r, i) => {
          const porV = Math.round(r.total / MOCK.nVehiculos);
          return (
            <div key={i} className="flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2">
                <r.Icon className="w-4 h-4 text-slate-400" />
                <span className="text-slate-700 font-medium">{r.label}</span>
              </div>
              <div className="text-right">
                <span className="text-xs text-slate-500">{eur(porV)}/veh × {MOCK.nVehiculos} = </span>
                <span className="font-bold text-emerald-600">{eur(r.total)}</span>
              </div>
            </div>
          );
        })}
        <div className="pt-3 mt-1 border-t border-slate-200 flex items-center justify-between">
          <span className="text-sm font-bold uppercase tracking-wider text-slate-700">Total estimado</span>
          <span className="text-lg font-black text-emerald-600">{eur(MOCK.ahorroTotal)} /año</span>
        </div>
      </div>

      {/* CTAs */}
      <div className="space-y-3 pt-2">
        <button
          className="w-full px-8 py-5 rounded-xl font-black text-lg text-white shadow-xl shadow-emerald-500/30"
          style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
        >
          Ver todo lo que incluye →
        </button>
        <div
          className="rounded-lg p-4 flex items-start gap-3"
          style={{ background: "rgba(30,58,95,0.06)", border: "1px solid #1e3a5f" }}
        >
          <span className="text-xl leading-none mt-0.5">🧪</span>
          <div className="text-[13px] text-slate-700 leading-relaxed">
            <p><strong className="text-slate-900">Empezamos con un piloto de 45–60 días</strong> en una muestra de tu flota.</p>
            <p className="mt-1 text-slate-500">Sin compromiso de despliegue total hasta que los números sean reales en tu operación.</p>
          </div>
        </div>
        <p className="text-center text-xs text-slate-500">Sin compromiso. Sin presión. Solo números.</p>
        <button
          className="w-full rounded-lg border transition-all text-sm flex items-center justify-center gap-2 mt-2"
          style={{ background: "#1e3a5f", color: "white", borderColor: "#1e3a5f", padding: "13px 24px", fontWeight: 600 }}
        >
          <Phone className="w-4 h-4" /> Quiero el análisis personalizado — Reservar 30 minutos →
        </button>
        <button className="w-full text-sm text-slate-500 hover:text-slate-900 underline mt-2 flex items-center justify-center gap-1">
          <Download className="w-3.5 h-3.5" /> Descargar este resumen en PDF
        </button>
      </div>
    </div>
  </main>
);

// ============================================
// MOCKUP PRINCIPAL
// ============================================
export default function MockupCalcTotal() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <HeaderMobilsafe />
      <div className="bg-emerald-50/40 border-b border-emerald-100 px-6 py-2.5">
        <p className="max-w-7xl mx-auto text-xs text-slate-600 text-center">
          <strong className="text-slate-900">Mockup nueva calculadora</strong> · Tema claro · Logo MOBILSAFE oficial · Panel lateral grande con contorno (320 px, fondo emerald, borde, sombra, TOTAL text-6xl)
        </p>
      </div>

      <div className="flex max-w-7xl mx-auto">
        <PanelLateralNuevo />
        <ContenidoP5 />
      </div>
    </div>
  );
}
